import { withJsonFormsControlProps } from "@jsonforms/react";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { GlobalConfigContext } from "../../../../context/GlobalConfigContext";
import EmailChipsComponent from "../../emailChips/EmailChipsControl";
import BoosterTable from "../shared/BoosterTable";
import ProductMultiplierConfig from "../shared/ProductMultiplierConfig";
import TierMultiplierConfig from "../shared/TierMultiplierConfig";
import "../shared/pointsOutcome.scss";

/**
 * AwardPurchaseBasedPoints — outcome component for "award-purchase-based-points"
 *
 * Stores in rule_data.points_calculation:
 * {
 *   rule_type: "purchase_based",
 *   tier_multipliers_enabled: boolean,
 *   tier_multiplier_overrides: [...],  // only time-bounded overrides; defaults come from useTiers
 *   product_multipliers: [...],
 *   booster_multipliers: [...],
 *   booster_flat_points: [...],
 *   points_cap_alert: { enabled: boolean, points_cap: number },
 *   fraud_alert_recipients: string  // comma-separated emails
 * }
 */
const AwardPurchaseBasedPoints = (props) => {
  const { handleChange, path, data } = props;
  const { programConfigurations } = useContext(GlobalConfigContext);

  // Initialize state from existing data
  // data is already scoped to points_calculation by JsonForms
  // Handle legacy double-nested structure: data may be { points_calculation: { ... } }
  const pointsCalc = data?.points_calculation || data || {};

  const [tierMultipliersEnabled, setTierMultipliersEnabled] = useState(
    pointsCalc.tier_multipliers_enabled || false
  );

  // Default tier multipliers are read-only from useTiers — only time-bounded
  // overrides are stored in tier_multiplier_overrides.
  const initialTimeBoundTierMultipliers = useMemo(() => {
    return (pointsCalc.tier_multiplier_overrides || []).filter(
      (m) => m.effective_start_date
    );
  }, [pointsCalc.tier_multiplier_overrides]);

  const [timeBoundTierMultipliers, setTimeBoundTierMultipliers] = useState(
    initialTimeBoundTierMultipliers
  );
  const [productMultipliers, setProductMultipliers] = useState(
    pointsCalc.product_multipliers || []
  );
  const [boosterMultipliers, setBoosterMultipliers] = useState(
    pointsCalc.booster_multipliers || []
  );
  const [boosterFlatPoints, setBoosterFlatPoints] = useState(
    pointsCalc.booster_flat_points || []
  );
  const [pointsCapEnabled, setPointsCapEnabled] = useState(
    pointsCalc.points_cap_alert?.enabled || false
  );
  const [pointsCapValue, setPointsCapValue] = useState(
    pointsCalc.points_cap_alert?.points_cap ?? ""
  );
  const [fraudAlertRecipients, setFraudAlertRecipients] = useState(
    pointsCalc.fraud_alert_recipients || ""
  );
  const [childErrors, setChildErrors] = useState({
    booster: false,
    product: false,
    tier: false,
  });

  const hasValidationErrors = Object.values(childErrors).some(Boolean);

  const handleBoosterValidation = useCallback((hasErrors) => {
    setChildErrors((prev) => ({ ...prev, booster: hasErrors }));
  }, []);
  const handleProductValidation = useCallback((hasErrors) => {
    setChildErrors((prev) => ({ ...prev, product: hasErrors }));
  }, []);
  const handleTierValidation = useCallback((hasErrors) => {
    setChildErrors((prev) => ({ ...prev, tier: hasErrors }));
  }, []);

  // Read base_earn_rate from program configuration
  const baseEarnRate = useMemo(() => {
    const config = programConfigurations?.find(
      (c) => c.key === "global_points_configuration"
    );
    const data = config?.value?.data;
    if (data?.base_earn_rate) return data.base_earn_rate;
    // Fallback: check joy_points
    if (data?.joy_points?.points) return data.joy_points.points;
    return null;
  }, [programConfigurations]);

  // Sync state to parent form data
  const syncData = useCallback(() => {
    const updated = {
      rule_type: "purchase_based",
      tier_multipliers_enabled: tierMultipliersEnabled,
      tier_multiplier_overrides: timeBoundTierMultipliers,
      product_multipliers: productMultipliers,
      booster_multipliers: boosterMultipliers,
      booster_flat_points: boosterFlatPoints,
      points_cap_alert: {
        enabled: pointsCapEnabled,
        points_cap: Number(pointsCapValue) || 0,
      },
      fraud_alert_recipients: pointsCapEnabled ? fraudAlertRecipients : "",
      _hasValidationErrors: hasValidationErrors,
    };
    handleChange(path, updated);
  }, [
    handleChange,
    path,
    tierMultipliersEnabled,
    timeBoundTierMultipliers,
    productMultipliers,
    boosterMultipliers,
    boosterFlatPoints,
    pointsCapEnabled,
    pointsCapValue,
    fraudAlertRecipients,
    hasValidationErrors,
  ]);

  useEffect(() => {
    syncData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tierMultipliersEnabled,
    timeBoundTierMultipliers,
    productMultipliers,
    boosterMultipliers,
    boosterFlatPoints,
    pointsCapEnabled,
    pointsCapValue,
    fraudAlertRecipients,
    hasValidationErrors,
  ]);

  return (
    <div className="points-outcome">
      {/* Basic Configuration — Base Earn Rate */}
      <div className="section-card basic-config">
        <h3 className="section-title">Purchase-based configuration</h3>
        <div className="base-earn-rate">
          <span className="rate-label">Base earn rate</span>
          <span className="rate-value">
            {baseEarnRate
              ? `${baseEarnRate} point${baseEarnRate > 1 ? "s" : ""} for every $1 spend`
              : "Not configured"}
          </span>
        </div>
      </div>

      {/* Tier Based Multiplier */}
      <TierMultiplierConfig
        enabled={tierMultipliersEnabled}
        onToggle={setTierMultipliersEnabled}
        timeBoundMultipliers={timeBoundTierMultipliers}
        onChangeTimeBound={setTimeBoundTierMultipliers}
        onValidationChange={handleTierValidation}
      />

      {/* Product / Product Group Rule Configuration */}
      <ProductMultiplierConfig
        productMultipliers={productMultipliers}
        onChange={setProductMultipliers}
        onValidationChange={handleProductValidation}
      />

      {/* Time Based Booster */}
      <BoosterTable
        title="Time-Bound Booster"
        description="Configure campaign-specific boosters that temporarily increase points or add bonus flat points."
        boosterMultipliers={boosterMultipliers}
        boosterFlatPoints={boosterFlatPoints}
        onChangeMultipliers={setBoosterMultipliers}
        onChangeFlatPoints={setBoosterFlatPoints}
        onValidationChange={handleBoosterValidation}
      />

      {/* Points Cap Alert */}
      <div className="section-card points-cap-section">
        <h3 className="section-title">Total Order Points</h3>
        <p className="cap-description">
          This alert does not prevent the customer from earning points. If calculated
          points for an order or receipt exceed this configured value, it triggers a
          fraud centre flag. Configure a cap at least at or equal to the configured
          cap.
        </p>
        <div className="cap-toggle">
          <FormControlLabel
            control={
              <Checkbox
                checked={pointsCapEnabled}
                onChange={(e) => setPointsCapEnabled(e.target.checked)}
                size="small"
              />
            }
            label="Total Order or Receipt Points Cap Alert"
          />
        </div>
        {pointsCapEnabled && (
          <>
            <div className="cap-input">
              <TextField
                size="small"
                type="number"
                fullWidth
                label="Points Cap"
                placeholder="Points Cap"
                value={pointsCapValue}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === "" || Number(val) >= 0) setPointsCapValue(val);
                }}
                inputProps={{ min: 0 }}
              />
            </div>
            <div className="cap-input" style={{ marginTop: "16px" }}>
              <EmailChipsComponent
                label="Fraud Alert Recipients (optional)"
                placeholder="Enter email addresses and press Enter"
                data={fraudAlertRecipients}
                handleChange={(_path, value) => setFraudAlertRecipients(value)}
                path="fraud_alert_recipients"
                enabled
                visible
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const AwardPurchaseBasedPointsControl = withJsonFormsControlProps(
  AwardPurchaseBasedPoints
);

export default AwardPurchaseBasedPoints;
